import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { AdminStationPortalLogRecord } from "../../../services/StationPortalLogService";
import LogRowDetail from "./LogRowDetail";

// Cột chẩn đoán lỗi trước — bày cả 12 cột trên màn 1440px thì mỗi cột còn
// ~100px, đường dẫn bị cắt cụt nên bày ra cũng không đọc được.
//
// `message` nằm trong bộ mặc định vì bảng đang chạy trên bản thật có cột đó, và
// với dòng `level: error` thì nội dung lỗi là thứ người trực cần trước nhất —
// bỏ nó xuống nhóm tuỳ chọn nghĩa là mặc định không ai đọc được lỗi. Đặt cuối vì
// đây là cột dài và biến thiên nhất; bị cắt bằng ellipsis, bấm vào dòng là xem đủ.
export const DEFAULT_COLUMN_KEYS = [
	"occurredAt",
	"level",
	"eventType",
	"action",
	"requestPath",
	"statusCode",
	"message",
] as const;

export const OPTIONAL_COLUMN_KEYS = ["partner", "actor", "sourceApp", "ipAddress", "userAgent"] as const;

export type ColumnKey = typeof DEFAULT_COLUMN_KEYS[number] | typeof OPTIONAL_COLUMN_KEYS[number];

// Nhãn cột tiếng Anh theo đặc tả — repo không có bản dịch riêng cho nhãn cột kỹ thuật này.
export const COLUMN_LABELS: Record<ColumnKey, string> = {
	occurredAt: "Time",
	level: "Level",
	eventType: "Event type",
	action: "Action",
	requestPath: "Path",
	statusCode: "Status",
	message: "Message",
	partner: "Station",
	actor: "Actor",
	sourceApp: "Source",
	ipAddress: "IP",
	userAgent: "User agent",
};

/** Khoá lưu lựa chọn cột ở máy người dùng. */
export const COLUMN_PREF_KEY = "station-portal-logs-columns";

// Mảng thường (không phải readonly tuple) để .filter()/.concat() trả về đúng
// kiểu ColumnKey[] mà không phải ép kiểu lại ở nhiều chỗ gọi.
const DEFAULT_LIST: ColumnKey[] = DEFAULT_COLUMN_KEYS.slice();
const OPTIONAL_LIST: ColumnKey[] = OPTIONAL_COLUMN_KEYS.slice();

function isOptionalColumnKey(key: string): key is ColumnKey {
	return OPTIONAL_LIST.indexOf(key as ColumnKey) !== -1;
}

/**
 * Đọc lựa chọn cột đã lưu ở localStorage. Khoá này từng được bản cũ ghi bằng
 * định dạng khác (hoặc người dùng tự sửa tay trong DevTools) — chuỗi đọc ra có
 * thể là bất cứ gì. BẮT BUỘC không bao giờ ném lỗi ra ngoài khiến màn hình
 * trắng; mọi nhánh hỏng đều rơi về bộ cột mặc định.
 */
function readColumnPreference(): ColumnKey[] {
	if (typeof window === "undefined") return DEFAULT_LIST.slice();
	try {
		// Chính việc ĐỌC thuộc tính `window.localStorage` cũng ném SecurityError khi
		// trình duyệt chặn toàn bộ dữ liệu trang (Safari riêng tư, Chrome chặn cookie
		// bên thứ ba trong iframe) — nên nó phải nằm TRONG try, không phải ở guard
		// phía trên. Để ngoài thì đúng ca spec cấm: màn hình trắng vì lỗi ném ra.
		if (!window.localStorage) return DEFAULT_LIST.slice();
		const raw = window.localStorage.getItem(COLUMN_PREF_KEY);
		if (!raw) return DEFAULT_LIST.slice();
		const parsed = JSON.parse(raw);
		if (!Array.isArray(parsed)) return DEFAULT_LIST.slice();
		const validOptional = parsed.filter(
			(key) => typeof key === "string" && isOptionalColumnKey(key)
		) as ColumnKey[];
		return DEFAULT_LIST.concat(validOptional);
	} catch (_err) {
		// JSON.parse ném lỗi trên chuỗi rác, hoặc localStorage bị chặn (chế độ ẩn
		// danh) — cả hai đều rơi về mặc định, không để lộ lỗi ra UI.
		return DEFAULT_LIST.slice();
	}
}

function writeColumnPreference(columns: ColumnKey[]) {
	if (typeof window === "undefined") return;
	try {
		if (!window.localStorage) return;
		window.localStorage.setItem(COLUMN_PREF_KEY, JSON.stringify(columns));
	} catch (_err) {
		// Quota đầy hoặc trình duyệt chặn ghi (chế độ ẩn danh) — bỏ qua. Lựa chọn
		// cột vẫn đúng trong phiên hiện tại, chỉ không được nhớ cho lần sau.
	}
}

/**
 * Hook co-located quản lý lựa chọn cột + đồng bộ localStorage.
 *
 * Tách khỏi component LogTable (nhận props thuần, controlled) để LogTable dễ
 * test độc lập bằng props giả không đụng localStorage thật. Hook này mới là
 * nơi thật sự đọc/ghi localStorage; vỏ màn hình ở Task 7C sẽ gọi hook này để
 * tạo `visibleColumns`/`onToggleColumn` truyền xuống LogTable.
 */
export function useColumnPreferences(): {
	visibleColumns: ColumnKey[];
	toggleColumn: (key: ColumnKey) => void;
} {
	const [visibleColumns, setVisibleColumns] = useState<ColumnKey[]>(() => readColumnPreference());

	const toggleColumn = useCallback((key: ColumnKey) => {
		if (!isOptionalColumnKey(key)) return; // Cột mặc định luôn hiện, không cho tắt.
		setVisibleColumns((prev) => (prev.indexOf(key) !== -1 ? prev.filter((k) => k !== key) : prev.concat(key)));
	}, []);

	// Ghi ở effect chứ KHÔNG ghi bên trong updater của setState: updater phải là
	// hàm thuần. React 17 gọi nó đúng một lần nên ghi trong đó vẫn chạy, nhưng
	// React 18 + StrictMode gọi hai lần — sẽ ghi hai lần. Ghi ở đây cũng chạy một
	// lượt lúc mount, ghi đè khoá đã lưu bằng những khoá bản này thật sự hiểu (khoá
	// lạ bị `readColumnPreference` loại từ trước, nên không mất gì đang dùng được).
	// Lưu ý: KHÔNG khử trùng lặp — `["actor","actor"]` được giữ nguyên; bảng vẫn vẽ
	// đúng vì `columns` dựng từ OPTIONAL_LIST chứ không từ mảng này.
	useEffect(() => {
		writeColumnPreference(visibleColumns);
	}, [visibleColumns]);

	return { visibleColumns, toggleColumn };
}

/** Nhãn actor: ưu tiên tên+email người đăng nhập, rồi email, rồi tên, rồi actorEmail ghi kèm log, cuối cùng là id thô. */
export function getActorLabel(log: AdminStationPortalLogRecord): string {
	if (log.user?.fullName && log.user?.email) return `${log.user.fullName} (${log.user.email})`;
	if (log.user?.email) return log.user.email;
	if (log.user?.fullName) return log.user.fullName;
	if (log.actorEmail) return log.actorEmail;
	if (log.userId) return `User #${log.userId}`;
	return "Anonymous";
}

/** Nhãn trạm: tên hiển thị (hoặc tên) kèm mã đối tác, để phân biệt được hai trạm trùng tên. */
export function getStationLabel(log: AdminStationPortalLogRecord): string {
	const name = log.partner?.displayName || log.partner?.name;
	if (name && log.partnerId) return `${name} (#${log.partnerId})`;
	if (name) return name;
	if (log.partnerId) return `#${log.partnerId}`;
	return "-";
}

/** Giờ vận hành neo theo giờ Việt Nam — đội trực đọc log theo giờ này, không phải UTC gốc của server. */
function formatOccurredAt(value: string | null | undefined): string {
	if (!value) return "-";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return value;
	return new Intl.DateTimeFormat("vi-VN", {
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
		hour: "2-digit",
		minute: "2-digit",
		second: "2-digit",
		hour12: false,
		timeZone: "Asia/Ho_Chi_Minh",
	}).format(date);
}

function renderCellValue(log: AdminStationPortalLogRecord, key: ColumnKey): React.ReactNode {
	switch (key) {
		case "occurredAt":
			return formatOccurredAt(log.occurredAt);
		case "level":
			return log.level ? log.level.toUpperCase() : "-";
		case "eventType":
			return log.eventType || "-";
		case "action":
			return log.action || "-";
		case "requestPath":
			return log.requestPath || log.route || "-";
		case "statusCode":
			return log.statusCode != null ? String(log.statusCode) : "-";
		case "message":
			return log.message || "-";
		case "partner":
			return getStationLabel(log);
		case "actor":
			return getActorLabel(log);
		case "sourceApp":
			return log.sourceApp || "-";
		case "ipAddress":
			return log.ipAddress || "-";
		case "userAgent":
			return log.userAgent || "-";
		default:
			return "-";
	}
}

// Bảng màu đồng bộ với TimeRangePicker.tsx và phần style nội tuyến của
// StationPortalLogs.tsx (xanh #0f62fe, viền #cbd5e1, chữ #334155/#0f172a,
// nền tiêu đề #f8fafc) — tránh sinh thêm bảng màu thứ ba trong cùng một màn.
const Wrap = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
`;

const ColumnPicker = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 16px;
	padding: 4px 2px;
`;

const PickerLabel = styled.label`
	display: inline-flex;
	align-items: center;
	gap: 6px;
	font-size: 13px;
	font-weight: 600;
	color: #334155;
	cursor: pointer;

	input {
		accent-color: #0f62fe;
		cursor: pointer;
	}
`;

const TableScroll = styled.div`
	overflow-x: auto;
`;

const Table = styled.table`
	width: 100%;
	border-collapse: collapse;
	min-width: 960px;
`;

const Th = styled.th`
	padding: 12px 10px;
	text-align: left;
	font-size: 13px;
	font-weight: 700;
	color: #334155;
	background: #f8fafc;
	border-bottom: 1px solid #e2e8f0;
	white-space: nowrap;
`;

const Td = styled.td`
	padding: 10px;
	font-size: 13px;
	color: #0f172a;
	vertical-align: top;
	max-width: 260px;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const Row = styled.tr<{ $open: boolean }>`
	border-top: 1px solid #e2e8f0;
	cursor: pointer;
	background: ${(props) => (props.$open ? "#eff6ff" : "#fff")};

	&:hover {
		background: ${(props) => (props.$open ? "#eff6ff" : "#f8fafc")};
	}
`;

const DetailRow = styled.tr`
	background: #f8fafc;
`;

const DetailCell = styled.td`
	padding: 0;
`;

export default function LogTable(props: {
	logs: AdminStationPortalLogRecord[];
	visibleColumns: ColumnKey[];
	onToggleColumn: (key: ColumnKey) => void;
}) {
	const [openId, setOpenId] = useState<number | null>(null);

	// Canonical hoá thứ tự: cột mặc định luôn đứng trước, cột tuỳ chọn giữ
	// đúng thứ tự OPTIONAL_COLUMN_KEYS bất kể prop visibleColumns truyền vào
	// theo thứ tự nào — tránh bảng nhảy cột lung tung.
	const activeOptional = OPTIONAL_LIST.filter((key) => props.visibleColumns.indexOf(key) !== -1);
	const columns: ColumnKey[] = DEFAULT_LIST.concat(activeOptional);

	const toggleRow = (id: number) => {
		setOpenId((prev) => (prev === id ? null : id));
	};

	return (
		<Wrap>
			<ColumnPicker
				role="group"
				aria-label="Optional columns">
				{OPTIONAL_LIST.map((key) => {
					const checked = props.visibleColumns.indexOf(key) !== -1;
					return (
						<PickerLabel key={key}>
							<input
								type="checkbox"
								checked={checked}
								onChange={() => props.onToggleColumn(key)}
							/>
							{COLUMN_LABELS[key]}
						</PickerLabel>
					);
				})}
			</ColumnPicker>

			<TableScroll>
				<Table>
					<thead>
						<tr>
							{columns.map((key) => (
								<Th key={key}>{COLUMN_LABELS[key]}</Th>
							))}
						</tr>
					</thead>
					<tbody>
						{
							// Bảng KHÔNG tự vẽ trạng thái rỗng. Màn hình đã có EmptyState phân
							// biệt ba ca (đang tải / hết dữ liệu / lọc mất hết) — thêm một ô câm
							// ở đây là dựng lại đúng cái mập mờ mà EmptyState sinh ra để diệt.
							// Vỏ màn hình chịu trách nhiệm render EmptyState thay cho bảng.
							props.logs.map((log) => {
								const isOpen = openId === log.id;
								return (
									<React.Fragment key={log.id}>
										<Row
											data-testid={`log-row-${log.id}`}
											$open={isOpen}
											tabIndex={0}
											aria-expanded={isOpen}
											onClick={() => toggleRow(log.id)}
											onKeyDown={(event) => {
												// Dòng là <tr> nên bàn phím không tự bấm được; Enter/Space
												// là quy ước cho phần tử đóng vai nút. preventDefault để
												// Space không cuộn trang thay vì mở chi tiết.
												// Chỉ nhận khi tiêu điểm nằm trên CHÍNH dòng: keydown nổi
												// bọt, nên sau này ai nhét <button>/<a>/<input> vào một ô
												// thì bấm Enter trên phần tử đó sẽ vừa bị chặn hành vi mặc
												// định vừa mở/đóng chi tiết ngoài ý muốn.
												if (event.target !== event.currentTarget) return;
												if (event.key === "Enter" || event.key === " ") {
													event.preventDefault();
													toggleRow(log.id);
												}
											}}>
											{columns.map((key) => (
												<Td key={key}>{renderCellValue(log, key)}</Td>
											))}
										</Row>
										{isOpen && (
											<DetailRow data-testid={`log-detail-${log.id}`}>
												<DetailCell colSpan={columns.length}>
													<LogRowDetail log={log} />
												</DetailCell>
											</DetailRow>
										)}
									</React.Fragment>
								);
							})
						}
					</tbody>
				</Table>
			</TableScroll>
		</Wrap>
	);
}
